// Public Privacy Policy page. Lives outside the auth gate so Apple's
// App Review team, Google's OAuth consent screen, and the App Store
// listing's Privacy URL field can all reach it without signing in.
//
// Two-audience rewrite 2026-08-28 (item f7d226bb): the original said
// "we do not collect data from people who are not authorized operators,"
// which stopped being true the day the Pro Hub launched. This version
// covers Operators, Pros, and the people whose information gets entered
// (tenants, prospects, referred people), discloses the Terms §9
// service-improvement use, and states the cookies position — VERIFIED
// 2026-08-28: zero analytics/advertising trackers in either hub; the Pro
// Hub stores its session in localStorage, the Owner Hub sets one
// first-party auth cookie. Essential-only, so no consent banner is
// required under GDPR/ePrivacy or US state law.
//
// IMPORTANT: This is a starting draft. The content matches what the
// iOS PrivacyInfo.xcprivacy declares (no tracking, no IDFA, no
// third-party analytics) and covers GDPR/CCPA basics, but for
// production reliance Peter should have counsel review.
import { bareHost, OWNER_HUB_ORIGIN, PRO_HUB_ORIGIN } from '@/lib/hosts';
import { PRODUCT_NAME } from '@/lib/brand';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-brand-fog">
      <header className="px-6 pt-12 pb-6 text-center border-b border-brand-mist">
        <a href="/" className="inline-block">
          <h1 className="font-display text-3xl text-brand-deep">{PRODUCT_NAME}</h1>
        </a>
        <p className="text-sm text-brand-slate mt-1">Privacy Policy</p>
      </header>

      <main className="px-6 py-10 max-w-3xl mx-auto">
        <article className="prose prose-sm sm:prose-base prose-headings:font-display prose-headings:text-brand-deep prose-p:text-brand-slate prose-li:text-brand-slate prose-a:text-image-pink hover:prose-a:text-image-pink-dark">

          <p className="text-xs uppercase tracking-[0.18em] text-brand-slate/70">
            Effective August 28, 2026
          </p>

          <p>
            This policy explains how we collect, use, and protect personal information when you use
            the Tenean iOS application, the Tenean TV app (Apple TV, Google TV and Samsung TVs), or the Tenean web products at <strong>{bareHost(OWNER_HUB_ORIGIN)}</strong> (the
            Owner Hub) and <strong>{bareHost(PRO_HUB_ORIGIN)}</strong> (the Pro Hub) (together, the
            &ldquo;Service&rdquo;). The Service is operated by PKNT Enterprises
            (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;).
          </p>

          <h2>1. Who this policy covers</h2>
          <ul>
            <li>
              <strong>Operators</strong> — salon-suite location owners and their staff, who use the
              Owner Hub and the iOS application to run their locations.
            </li>
            <li>
              <strong>Pros</strong> — independent salon professionals with a suite tenancy at a
              participating studio, who use the Pro Hub.
            </li>
            <li>
              <strong>People whose information is entered into the Service</strong> — such as a
              studio&rsquo;s tenants, leads and prospects, or the people named in a referral. We
              process that information on the studio&rsquo;s behalf; the studio is responsible for
              having the right to enter it.
            </li>
          </ul>
          <p>
            There is no public sign-up: every account is provided through a participating studio or
            an authorized administrator.
          </p>

          <h2>2. Information we collect</h2>
          <ul>
            <li><strong>Account info</strong> — email address, display name, role, and the locations you have access to.</li>
            <li><strong>Operator-entered records</strong> — tenant records (names, emails, phone numbers, addresses, lease terms, payment details, business details, emergency contacts, photos), maintenance tickets, onboarding records, financial snapshots, compliance records, and notes.</li>
            <li><strong>Pro-provided content</strong> — your profile photo, bio, business name, the services you offer, words or photos you contribute to a spotlight, documents you upload (such as insurance certificates), suggestions you submit, and the public business listing you choose to link.</li>
            <li><strong>Tenancy information from your studio</strong> — if you are a Pro, your studio provides us information about your tenancy (such as your suite and business name) from its own records.</li>
            <li><strong>Referrals</strong> — when someone submits a referral, we collect the name, email, and phone number of both the referrer and the person referred, to operate the studio&rsquo;s referral programme.</li>
            <li><strong>Messages</strong> — the contents of messages Operators send and receive through the Service (such as SMS and email with leads and tenants).</li>
            <li><strong>Voice input</strong> — when you use the AI assistant by voice on iOS, the recording is transcribed for that session. Audio is not retained beyond what&rsquo;s needed to fulfill your request.</li>
            {/* Tenean TV — added 2026-10-11 with the Apple TV App Store submission (open item da2c29d4); the same facts as App
                Store Connect's Apple TV privacy text and the Apple TV app's privacy manifest. The effective date is left as it
                is on purpose: it is TERMS_VERSION (ConsentGate), and moving it would ask every pro to accept the terms again. */}
            <li><strong>Tenean TV screens</strong> — a screen an Operator pairs with the Service sends us the pairing token we gave it and a short status report (its model, screen size, operating-system version, connection type, app version, what it is playing, how much it has stored and its last error), along with the network address it connects from, so the Operator can look after its screens. Nobody signs in on a screen, and nothing is collected about the people who watch it. A program may show QR codes; scanning one opens a web page on the viewer&rsquo;s own phone, under that page&rsquo;s own policy.</li>
            <li><strong>Device &amp; technical info</strong> — app version, OS version, and crash logs. Crash logs are stored locally and only transmitted with your consent.</li>
          </ul>
          <p>
            We <strong>do not</strong> collect advertising identifiers (IDFA), browsing history outside
            the Service, biometric identifiers, or location data beyond what you explicitly attach to
            a record in the Service.
          </p>

          <h2>3. Cookies and local storage</h2>
          <p>
            The Service uses only what sign-in requires: the Pro Hub keeps your session in your
            browser&rsquo;s local storage, and the Owner Hub sets a first-party authentication
            cookie. That is the whole list. We set <strong>no advertising cookies, no third-party
            analytics cookies, and no cross-site trackers</strong> — which is why the Service does
            not show a cookie-consent banner: those banners exist for non-essential tracking, and
            there is none here.
          </p>

          <h2>4. How we use information</h2>
          <ul>
            <li>To operate the Service&rsquo;s features — for Operators, running their locations; for Pros, the profile, spotlight, QR code, reviews, referral, and studio-access features you use.</li>
            <li>To publish content where a feature says so — for example a spotlight a studio sends to its social channels.</li>
            <li>To run the AI-assisted features when you use them.</li>
            <li>To deliver notifications you&rsquo;ve enabled.</li>
            <li>To detect and respond to security incidents.</li>
            <li>
              <strong>To improve the Service</strong> — as described in Section 9 of the
              {' '}<a href="/terms">Terms of Service</a>, we may use usage information and data
              derived from content and records to improve the Service and develop new features,
              including its AI-assisted features. Where this crosses studios or users, the data is
              de-identified and/or aggregated so it does not identify you, your business, your
              studio, or your clients.
            </li>
          </ul>
          <p>
            We do not use your information for advertising, we do not sell it, and we do not share
            it with data brokers.
          </p>

          <h2>5. Who we share information with</h2>
          <p>
            Your data is processed by these service providers, each under contractual
            confidentiality and security obligations:
          </p>
          <ul>
            <li><strong>Supabase, Inc.</strong> — backend database, authentication, and file storage. Encrypted at rest and in transit.</li>
            <li><strong>Anthropic, PBC</strong> — large language model inference for the AI-assisted features, when you use them.</li>
            <li><strong>Google LLC</strong> — &ldquo;Sign in with Google&rdquo; when you choose that option, and calendar/business-profile integrations a studio connects.</li>
            <li><strong>Apple, Inc.</strong> — push notification delivery (APNs) when you&rsquo;ve enabled notifications.</li>
            <li><strong>The studio&rsquo;s own providers</strong> — a participating studio&rsquo;s CRM and messaging providers, when the studio directs the Service to send or sync through them.</li>
          </ul>
          <p>
            Two sharing relationships are part of how the product works, not third-party
            processing: <strong>a Pro&rsquo;s studio</strong> sees the tenancy-related information a
            Pro maintains in the Pro Hub (that is what the hub is for), and <strong>social
            networks</strong> receive content when a studio publishes it to them. We do not share
            data with advertisers, data brokers, or analytics platforms.
          </p>

          <h2>6. Data retention</h2>
          <p>
            We retain your data for as long as your account is active. If your account is
            deactivated — including when a Pro&rsquo;s tenancy ends — your data is retained for up
            to 90 days in case of reinstatement, after which it is deleted from active systems.
            Encrypted backups may persist for up to 30 additional days before they expire. Content
            that was published or printed while your account was active (such as a spotlight post or
            printed signage) may persist in those forms.
          </p>

          <h2>7. Your rights</h2>
          <p>
            Depending on your jurisdiction (including the EU/UK under GDPR and California under CCPA),
            you may have the following rights:
          </p>
          <ul>
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Correction</strong> — edit your information directly in the Service; Pros can correct most of their profile in the Pro Hub.</li>
            <li><strong>Deletion</strong> — request deletion of your account and associated data.</li>
            <li><strong>Portability</strong> — receive your data in a machine-readable format.</li>
            <li><strong>Objection</strong> — object to processing for direct marketing (we do not do this).</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at the address below. We will respond within 30
            days. If your information was entered by a studio (for example, you are a studio&rsquo;s
            tenant or a referred person), we may refer your request to that studio, since the data is
            theirs to manage.
          </p>

          <h2>8. Children's privacy</h2>
          <p>
            Tenean is a business application for adults. We do not knowingly collect personal
            information from anyone under the age of 16. If you believe a child has provided us with
            personal information, please contact us so we can delete it.
          </p>

          <h2>9. International transfers</h2>
          <p>
            Tenean is operated from the United States. If you access the Service from outside the
            United States, your information may be transferred to, stored, and processed in the United
            States. By using the Service, you consent to this transfer.
          </p>

          <h2>10. Security</h2>
          <p>
            We use industry-standard administrative, technical, and physical safeguards to protect your
            information. All network traffic to our backend is encrypted with TLS. Authentication uses
            short-lived JSON Web Tokens. Database access is gated by row-level security policies that
            scope each account to its own data. No system is perfectly secure; we cannot guarantee
            absolute security.
          </p>

          <h2>11. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The &ldquo;Effective&rdquo; date at the
            top of the page shows when the most recent version took effect. We will notify users of
            material changes through the Service or by email.
          </p>

          <h2>12. Contact</h2>
          <p>
            For privacy questions or to exercise any of the rights described above:
          </p>
          <address className="not-italic">
            PKNT Enterprises — Privacy<br />
            15 Cattail Lane<br />
            Sharon, MA 02067 USA<br />
            <a href="mailto:plewis@pkntindustries.com">plewis@pkntindustries.com</a>
          </address>

        </article>

        <div className="mt-12 pt-6 border-t border-brand-mist text-center">
          <a href="/" className="text-sm text-brand-slate hover:text-image-pink">
            ← Back to {PRODUCT_NAME}
          </a>
          <p className="mt-3 text-xs text-brand-slate/70">© 2026 Cadence Advisers</p>
        </div>
      </main>
    </div>
  );
}
